import _ from 'lodash';
import { AnalyticsLoadingState, } from 'reducers/types';
import { createSelector } from 'reselect';
import { isDefaultCategory } from 'hooks/useCategory';
import { sortByName } from 'utils/sort';
import { createNonMemoizedSelector } from 'store/selectors';
import { selectNormalizedCategories } from 'reducers/selectors';
import { emptyArray } from '../../constants';
import { selectMonthlyTotals } from '../analytics/selectors';
export const selectUserIncomes = (state) => state.user.incomes;
export const selectBudgets = (state) => state.budgeting.budgets;
export const selectOverallBudget = (state) => {
    if (state.budgeting.budgets && state.budgeting.budgets.length) {
        return state.budgeting.budgets.find((budget) => budget.key === 'overall.monthly');
    }
    return undefined;
};
export const selectPrefilledAverageBudgets = createSelector([selectBudgets, selectNormalizedCategories], (baseBudgets, categories) => {
    let prefilledOverallLimit = 0;
    const prefilledCategoryBudgets = baseBudgets.map((budgetCategory) => {
        if (budgetCategory.type === 'merchant' ||
            (budgetCategory.categoryId &&
                !categories[budgetCategory.categoryId]?.canSetBudget)) {
            return budgetCategory;
        }
        const { previousPeriodAverage } = budgetCategory;
        const newBaseLimit = previousPeriodAverage || 0;
        if (budgetCategory.type !== 'overall' && newBaseLimit) {
            prefilledOverallLimit += newBaseLimit;
        }
        return {
            ...budgetCategory,
            baseLimit: newBaseLimit,
            totalLimit: newBaseLimit,
            rollingAccumulatedLimit: null,
        };
    });
    return prefilledCategoryBudgets
        .map((budget) => {
        if (budget.type === 'overall' && prefilledOverallLimit) {
            const roundedLimit = Math.round(((prefilledOverallLimit || 0) + Number.EPSILON) * 100) /
                100;
            return {
                ...budget,
                baseLimit: roundedLimit,
                totalLimit: roundedLimit,
                previousAverage: roundedLimit,
                previousPeriodAverage: roundedLimit,
                rollingAccumulatedLimit: null,
            };
        }
        return budget;
    })
        .filter((budget) => budget.totalLimit && budget.totalLimit > 0);
});
export const selectOverallBudgetBaseLimit = (state) => selectOverallBudget(state)?.baseLimit;
export const selectMonthlyCategories = (state) => state.expenses.monthlyCategories;
export const selectMonthlyMerchants = (state) => state.expenses.monthlyMerchants;
export const selectPosition = (state) => state.expenses.budgetingPosition;
export const selectIncomes = createSelector([selectUserIncomes], (incomes) => {
    const income = incomes?.find((i) => i.isPrimary);
    return income;
});
export const selectMerchantBudgets = createSelector([selectBudgets], (budgets) => budgets.filter((budget) => budget.type === 'merchant'));
export const sortMonthlyTotals = (data) => [...data].sort((a, b) => {
    // Top spend is first
    if (a.total > b.total) {
        return 1;
    }
    if (a.total === b.total) {
        // If spend is equal sort by display name
        if (a.displayName < b.displayName)
            return -1;
        if (a.displayName > b.displayName)
            return 1;
        return 0;
    }
    return -1;
});
export const selectMonthlyTotalsWithBudgets = createSelector([selectMonthlyTotals], (monthlyTotals) => monthlyTotals.filter((total) => total.totalBudget != null));
const selectPeriodCategoriesState = createSelector([selectMonthlyTotalsWithBudgets, selectPosition, selectMonthlyCategories], (monthlyTotalsWithBudgets, position, monthlyCategories) => monthlyCategories[`${monthlyTotalsWithBudgets[position]?.from}|${monthlyTotalsWithBudgets[position]?.to}`]);
export const selectCategoriesState = createSelector([selectPeriodCategoriesState, selectNormalizedCategories], (categoriesState, normalizedCategories) => {
    if (!categoriesState)
        return undefined;
    return {
        ...categoriesState,
        categories: categoriesState?.categories.filter((category) => normalizedCategories[category.id]?.canSetBudget),
    };
});
export const selectExcludedParentCategories = createSelector([selectPeriodCategoriesState], (categoriesState) => categoriesState?.categories
    ? sortMonthlyTotals(categoriesState.categories.filter((category) => category.hideFromStats &&
        category.transactionsCount > 0 &&
        !category.parentCategoryId))
    : emptyArray);
export const selectCategories = createSelector([selectCategoriesState], (categoriesState) => categoriesState?.categories
    ? sortMonthlyTotals(categoriesState.categories)
    : emptyArray);
export const selectCategoriesLoadingState = createNonMemoizedSelector([selectCategoriesState], (categoriesState) => categoriesState?.loadingState ?? AnalyticsLoadingState.INITIAL);
export const selectMerchantsState = createSelector([selectMonthlyTotalsWithBudgets, selectPosition, selectMonthlyMerchants], (monthlyTotalsWithBudgets, position, monthlyMerchants) => monthlyTotalsWithBudgets[position]
    ? monthlyMerchants[`${monthlyTotalsWithBudgets[position].from}|${monthlyTotalsWithBudgets[position].to}`]
    : undefined);
export const selectMerchants = createSelector([selectMerchantsState], (merchantsState) => merchantsState?.merchants
    ? sortMonthlyTotals(merchantsState.merchants)
    : emptyArray);
export const selectMerchantsLoadingState = createNonMemoizedSelector([selectMerchantsState], (merchantsState) => merchantsState?.loadingState ?? AnalyticsLoadingState.INITIAL);
export const selectBudgetedMerchants = createSelector([selectMerchants, selectBudgets], (merchants) => merchants.filter((item) => item.budgetLimit));
export const makeSelectCategoryTotalById = () => createSelector([selectCategories, (_, id) => id], (categories, id) => categories.find((category) => category.id === id));
export const makeSelectMerchantTotalById = () => createSelector([selectBudgetedMerchants, (_, id) => id], (merchants, id) => merchants.find((merchant) => merchant.id === id));
export const selectBudgetedParentCategories = createSelector([selectCategories], (categories) => categories.filter((item) => item?.baseLimit != null && !item.parentCategoryId));
export const selectBudgetedSubCategoriesForParentCategory = createSelector([selectCategories, (_, parentCategoryId) => parentCategoryId], (categories, parentCategoryId) => sortMonthlyTotals(categories.filter((item) => item?.baseLimit != null && item.parentCategoryId === parentCategoryId)));
export const selectUnbudgetCategoriesForParentCategory = createSelector([
    selectCategories,
    (_, parentCategoryId) => parentCategoryId,
    selectPosition,
], (categories, parentCategoryId, position) => position === 0
    ? categories.filter((item) => !item.budgetLimit &&
        item.total !== 0 &&
        item.parentCategoryId === parentCategoryId)
    : EMPTY_CATEGORIES);
const EMPTY_CATEGORIES = [];
export const selectUnbudgetedCategoriesForCurrentPeriod = createSelector([selectCategories, selectPosition], (categories, position) => position === 0
    ? categories.filter((item) => !item.budgetLimit && item.total < 0 && !item.parentCategoryId)
    : EMPTY_CATEGORIES);
export const selectCategoryBudgets = createSelector([selectBudgets], (budgets) => {
    const array = sortByName(budgets.filter((budget) => budget.type === 'category'));
    const filtered = array.filter((item) => item.key.includes('category'));
    const grouped = _.groupBy(filtered, (d) => {
        if (!isDefaultCategory(d.key.replace('category.', ''))) {
            return 'My Categories';
        }
        return 'Categories';
    });
    return (grouped['My Categories'] || []).concat(grouped.Categories || []);
});
export const isRolloverActive = createSelector([selectBudgets], (budgets) => budgets.some((budget) => budget.shouldRollover));
